import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LanguageIcon from '@mui/icons-material/Language';
import { useLang } from '../../../utils/i18n';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MenuIcon from '@mui/icons-material/Menu';
import Searchbar from './Searchbar';
import MobileMenu from './MobileMenu';
import defaultLogo from '../../../assets/images/logo-starmart.webp';
import PrimaryDropDownMenu from './PrimaryDropDownMenu';
import SecondaryDropDownMenu from './SecondaryDropDownMenu';
import iconMap from '../../../utils/iconMap';
import useCartPricing from '../../../utils/useCartPricing';
import usePromoDiscount from '../../../utils/usePromoDiscount';

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// Renders one Row 2 (left) utility item based on its content type.
const UtilityItem = ({ item, lang }) => {
  const { Icon } = iconMap[item.icon] || {};
  const label = lang === 'zh' && item.labelZh ? item.labelZh : item.label;
  const inner = (
    <span className="flex items-center gap-1.5">
      {Icon && <Icon sx={{ fontSize: "18px" }} />}
      {label}
    </span>
  );

  if (item.contentType === "phone") return <a href={`tel:${item.value}`} className="text-gray-500 hover:text-primary-blue">{inner}</a>;
  if (item.contentType === "email") return <a href={`mailto:${item.value}`} className="text-gray-500 hover:text-primary-blue">{inner}</a>;
  if (item.contentType === "link") return <a href={item.value} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-primary-blue">{inner}</a>;
  return <span className="text-gray-500">{inner}</span>;
};

// Renders one Row 2 (right) nav link — internal links use React Router,
// external/absolute links use a plain anchor.
const NavItem = ({ item, lang }) => {
  const { Icon } = iconMap[item.icon] || {};
  const label = lang === 'zh' && item.labelZh ? item.labelZh : item.label;
  const inner = (
    <span className="flex items-center gap-1.5">
      {Icon && <Icon sx={{ fontSize: "18px" }} />}
      {label}
    </span>
  );

  if (item.url?.startsWith('/')) {
    return <Link to={item.url} className="hover:text-primary-blue">{inner}</Link>;
  }
  return <a href={item.url} target="_blank" rel="noreferrer" className="hover:text-primary-blue">{inner}</a>;
};

const Header = () => {

  const { isAuthenticated, user } = useSelector((state) => state.user);
  const { cartItems, promoCode } = useSelector(state => state.cart);
  const { lang, setLang } = useLang();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [togglePrimaryDropDown, setTogglePrimaryDropDown] = useState(false);
  const [toggleSecondaryDropDown, setToggleSecondaryDropDown] = useState(false);
  const primaryDropDownRef = useRef(null);
  const secondaryDropDownRef = useRef(null);

  // Closes either dropdown when the user clicks anywhere outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (primaryDropDownRef.current && !primaryDropDownRef.current.contains(e.target)) {
        setTogglePrimaryDropDown(false);
      }
      if (secondaryDropDownRef.current && !secondaryDropDownRef.current.contains(e.target)) {
        setToggleSecondaryDropDown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cartPricing = useCartPricing(cartItems);
  const afterOfferTotal = cartPricing ? cartPricing.total : cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const promoResult = usePromoDiscount(promoCode, afterOfferTotal);
  const cartTotal = promoResult?.valid ? Math.max(afterOfferTotal - promoResult.discountAmount, 0) : afterOfferTotal;

  // Sensible defaults shown until settings load (or if the request fails),
  // matching the original static design so nothing looks broken.
  const [topBarEnabled, setTopBarEnabled] = useState(true);
  const [topBarBgColor, setTopBarBgColor] = useState("#0284c7");
  const [topBarContent, setTopBarContent] = useState("Fresh groceries, delivered to your door. Free delivery on orders over HK$499.");
  const [topBarContentZh, setTopBarContentZh] = useState("");
  const [utilityLeftItems, setUtilityLeftItems] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoWidth, setLogoWidth] = useState(48);
  const [showWishlist, setShowWishlist] = useState(true);
  const [showCart, setShowCart] = useState(true);
  const [showUserProfile, setShowUserProfile] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(true);
  const [showWallet, setShowWallet] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get('/api/v1/header-settings');
        const s = data.settings;
        setTopBarEnabled(s.topBarEnabled);
        if (s.topBarBgColor) setTopBarBgColor(s.topBarBgColor);
        if (s.topBarContent) setTopBarContent(s.topBarContent);
        if (s.topBarContentZh) setTopBarContentZh(s.topBarContentZh);
        setUtilityLeftItems(s.utilityLeftItems || []);
        setNavItems(s.navItems || []);
        if (s.logo?.url) setLogoUrl(s.logo.url);

        // Apply an admin-uploaded favicon dynamically, if one exists —
        // otherwise the static default in public/favicon.ico stays as-is.
        if (s.favicon?.url) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = s.favicon.url;
        }
        if (s.logoWidth) setLogoWidth(s.logoWidth);
        setShowWishlist(s.showWishlist);
        setShowCart(s.showCart);
        setShowUserProfile(s.showUserProfile);
        setShowMoreMenu(s.showMoreMenu);
        setShowWallet(s.showWallet);
      } catch (error) {
        console.error('Failed to load header settings', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="w-full relative z-20">

      {/* Row 1: top bar */}
      {topBarEnabled && (
        <div
          className="text-white text-sm text-center font-medium py-2.5 px-2 [&_p]:m-0"
          style={{ backgroundColor: topBarBgColor }}
          dangerouslySetInnerHTML={{ __html: lang === 'zh' && topBarContentZh ? topBarContentZh : topBarContent }}
        />
      )}

      {/* Row 2: utility bar */}
      <div className="hidden sm:flex bg-white border-b text-sm text-gray-700 py-3">
        <div className="w-[95%] m-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            {utilityLeftItems.map((item, i) => (
              <UtilityItem item={item} lang={lang} key={i} />
            ))}
          </div>
          <div className="flex items-center gap-6">
            {navItems.map((item, i) => (
              <NavItem item={item} lang={lang} key={i} />
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: logo, search, account, cart */}
      <header className="bg-white border-b py-3 w-full">
        <div className="w-full sm:w-[95%] px-1 sm:px-4 m-auto flex justify-between items-center gap-2 sm:gap-4 relative">

          <button
            type="button"
            className="sm:hidden text-gray-700 p-1 flex-shrink-0"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon sx={{ fontSize: "26px" }} />
          </button>

          <Link to="/" className="flex-shrink-0">
            <img draggable="false" className="object-contain" style={{ width: `${logoWidth}px`, height: 'auto' }} src={logoUrl || defaultLogo} alt="Star Mart Logo" />
          </Link>

          <div className="flex-1">
            <Searchbar />
          </div>

          <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0 relative">

            {showUserProfile && (
                          <div className="hidden sm:block relative" ref={primaryDropDownRef}>
              {isAuthenticated === false ?
                  <Link to="/login" className="px-4 py-1.5 text-sm text-gray-700 border rounded-sm hover:border-primary-blue hover:text-primary-blue">Login</Link>
                  :
                  <span className="flex items-center gap-1 cursor-pointer text-gray-700" onClick={() => setTogglePrimaryDropDown(!togglePrimaryDropDown)}>
                    <PersonOutlineIcon sx={{ fontSize: "26px" }} />
                    <span>{togglePrimaryDropDown ? <ExpandLessIcon sx={{ fontSize: "16px" }} /> : <ExpandMoreIcon sx={{ fontSize: "16px" }} />}</span>
                  </span>
                }
                {togglePrimaryDropDown && <PrimaryDropDownMenu setTogglePrimaryDropDown={setTogglePrimaryDropDown} user={user} showWallet={showWallet} />}
              </div>
            )}

            {showMoreMenu && (
                          <div className="hidden sm:block relative" ref={secondaryDropDownRef}>
              <span className="flex items-center gap-1 cursor-pointer text-gray-700" onClick={() => setToggleSecondaryDropDown(!toggleSecondaryDropDown)}>
                  More
                  <span>{toggleSecondaryDropDown ? <ExpandLessIcon sx={{ fontSize: "16px" }} /> : <ExpandMoreIcon sx={{ fontSize: "16px" }} />}</span>
                </span>
                {toggleSecondaryDropDown && <SecondaryDropDownMenu />}
              </div>
            )}

            {showWishlist && (
              <Link to="/wishlist" className="hidden sm:block text-gray-700 hover:text-primary-blue">
                <FavoriteBorderIcon />
              </Link>
            )}

            {showCart && (
              <Link to="/cart" className="flex items-center gap-2 relative text-gray-700">
                <span className="relative">
                  <ShoppingCartIcon />
                  {cartItems.length > 0 &&
                    <div className="w-4 h-4 p-2 bg-red-500 text-xs text-white rounded-full absolute -top-2 -right-2 flex justify-center items-center">
                      {cartItems.length}
                    </div>
                  }
                </span>
                <span className="font-medium hidden sm:inline">HK${cartTotal.toLocaleString()}</span>
              </Link>
            )}

            <button
              type="button"
              onClick={() => setLang(lang === 'en' ? 'zh' : 'en')}
              className="flex items-center gap-1 text-gray-700 hover:text-primary-orange"
              title={lang === 'en' ? '切換到中文' : 'Switch to English'}
            >
              <LanguageIcon />
              <span className="text-xs font-semibold hidden sm:inline">{lang === 'en' ? '中文' : 'EN'}</span>
            </button>
          </div>

        </div>
      </header>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        user={user}
      />

    </div>
  )
};

export default Header;
